import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

export default function FuelSettings() {
  const [s, setS] = useState(null);
  const [c, setC] = useState(null);

  useEffect(() => {
    api.get("/fuel/settings").then((r) => setS(r.data)).catch((e) => toast.error(errMsg(e)));
    api.get("/company").then((r) => setC(r.data)).catch(() => {});
  }, []);

  const saveCompany = async (e) => {
    e.preventDefault();
    try { const r = await api.put("/company", c); setC(r.data); toast.success("Company details saved"); } catch (err) { toast.error(errMsg(err)); }
  };

  const save = async (e) => {
    e.preventDefault();
    try {
      const r = await api.put("/fuel/settings", {
        opening_stock_l: parseFloat(s.opening_stock_l || 0),
        low_stock_threshold_l: parseFloat(s.low_stock_threshold_l || 0),
        diesel_rate_per_l: parseFloat(s.diesel_rate_per_l || 0),
      });
      setS(r.data);
      toast.success("Settings saved");
    } catch (err) { toast.error(errMsg(err)); }
  };

  if (!s) return null;
  const f = (key, label, hint) => (
    <div className="space-y-2">
      <Label>{label}</Label>
      <Input data-testid={`fuel-setting-${key}`} type="number" step="0.01" min="0" value={s[key]} onChange={(e) => setS({ ...s, [key]: e.target.value })} />
      <p className="text-xs text-muted-foreground">{hint}</p>
    </div>
  );

  return (
    <div className="space-y-6 animate-fade-up max-w-2xl" data-testid="fuel-settings-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight">Settings</h1>
        <p className="text-muted-foreground mt-1">Company profile for invoices, diesel tank configuration and default rates.</p>
      </div>
      {c && (
        <Card className="bg-card border-border">
          <CardHeader><CardTitle className="text-lg">Company Profile (printed on invoices)</CardTitle></CardHeader>
          <CardContent>
            <form onSubmit={saveCompany} className="space-y-4">
              <div className="space-y-2"><Label>Company Name</Label><Input data-testid="company-name-input" required value={c.name} onChange={(e) => setC({ ...c, name: e.target.value })} /></div>
              <div className="space-y-2"><Label>Address</Label><Input data-testid="company-address-input" value={c.address} onChange={(e) => setC({ ...c, address: e.target.value })} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>State <span className="text-xs text-muted-foreground">(decides CGST/SGST vs IGST)</span></Label><Input data-testid="company-state-input" value={c.state} onChange={(e) => setC({ ...c, state: e.target.value })} placeholder="Uttar Pradesh" /></div>
                <div className="space-y-2"><Label>GSTIN</Label><Input data-testid="company-gstin-input" value={c.gstin} onChange={(e) => setC({ ...c, gstin: e.target.value.toUpperCase() })} maxLength={15} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2"><Label>Phone</Label><Input data-testid="company-phone-input" value={c.phone} onChange={(e) => setC({ ...c, phone: e.target.value })} /></div>
                <div className="space-y-2"><Label>Email</Label><Input data-testid="company-email-input" value={c.email} onChange={(e) => setC({ ...c, email: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Bank Details</Label><Textarea data-testid="company-bank-input" rows={3} value={c.bank_details} onChange={(e) => setC({ ...c, bank_details: e.target.value })} placeholder="Bank, A/c No, IFSC" /></div>
              <div className="space-y-2"><Label>Terms / Footer Note</Label><Textarea data-testid="company-terms-input" rows={2} value={c.terms} onChange={(e) => setC({ ...c, terms: e.target.value })} /></div>
              <Button type="submit" data-testid="company-save-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Save Company</Button>
            </form>
          </CardContent>
        </Card>
      )}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="text-lg">Diesel Tank</CardTitle></CardHeader>
        <CardContent>
          <form onSubmit={save} className="space-y-5">
            {f("opening_stock_l", "Opening Stock (L)", "Diesel on hand before the first inward entry.")}
            {f("low_stock_threshold_l", "Low Stock Alert (L)", "Stock at or below this level is flagged as low.")}
            {f("diesel_rate_per_l", "Default Diesel Rate (₹ / L)", "Used when an inward entry has no rate.")}
            <Button type="submit" data-testid="fuel-settings-save-btn" className="bg-sky-500 hover:bg-sky-600 text-white">Save Settings</Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
